import { useCallback, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import toast from "react-hot-toast";
import {
  IBroadcastFormShape,
  IBroadcastTranslationShape
} from "../interfaces/esim/BroadcastNotifications.types";
import { translateAIContent } from "../services/AIContent.service";
import {
  buildTranslationSignature,
  describeGenerationOutcome,
  validateBroadcastSource
} from "../utils/esim/broadcastGenerationOutcome";
import {
  clean,
  IMergeResult,
  mergeBroadcastTranslations,
  pendingTranslationTargets
} from "../utils/esim/mergeBroadcastTranslations";

// Drives the "Gjenero për të gjitha gjuhët" button: guards the source text, decides which languages
// to ask for, then folds the result into the translation field array in one mutation. Bypasses
// useFetch deliberately — that is for fetch-on-mount with params diffing, not a one-shot POST.
const useBroadcastTranslate = (
  sourceLanguage: string,
  applyRows: (rows: IBroadcastTranslationShape[]) => void
) => {
  const { getValues } = useFormContext<IBroadcastFormShape>();
  const [generating, setGenerating] = useState(false);
  const [lastResult, setLastResult] = useState<IMergeResult | null>(null);
  // Backend notes: languages the model skipped, text shortened to fit the columns.
  const [lastWarnings, setLastWarnings] = useState<string[]>([]);
  // What the current translations were generated from; null until a first successful run.
  const [lastSignature, setLastSignature] = useState<string | null>(null);

  // React 18 batches state: two clicks in one tick would both see generating === false.
  const busy = useRef(false);

  const generate = useCallback(async () => {
    if (busy.current) return;

    const title = clean(getValues("defaultTitle"));
    const body = clean(getValues("defaultBody"));
    const sourceError = validateBroadcastSource(title, body);

    if (sourceError) {
      toast.error(sourceError);
      return;
    }

    // A changed source makes every existing translation stale, so this run replaces them all.
    const signature = buildTranslationSignature(sourceLanguage, title, body);
    const regenerate = lastSignature !== null && lastSignature !== signature;

    const requested = pendingTranslationTargets(
      getValues("translations") || [],
      sourceLanguage,
      regenerate
    );

    if (!requested.length) {
      toast.success("Të gjitha gjuhët kanë tashmë përkthim");
      return;
    }

    busy.current = true;
    setGenerating(true);

    try {
      const res = await translateAIContent({
        sourceLanguageCode: sourceLanguage,
        targetLanguageCodes: requested,
        title,
        body
      });

      // Shape-check first: the existing AI modal crashes on a well-formed but empty response.
      const payload = res && res.data ? res.data : undefined;
      const incoming = payload ? payload.translations : undefined;
      setLastWarnings(payload && Array.isArray(payload.warnings) ? payload.warnings : []);

      if (!Array.isArray(incoming) || !incoming.length) {
        toast.error("Përgjigja e AI-t nuk përmbante përkthime");
        return;
      }

      // Re-read AFTER the await: text typed mid-request is protected by the keep-filled branch.
      const result = mergeBroadcastTranslations({
        current: getValues("translations") || [],
        incoming,
        sourceLanguage,
        requested,
        overwriteExisting: regenerate
      });

      setLastResult(result);

      if (!result.appliedCodes.length) {
        toast.error("Nuk u aplikua asnjë përkthim i vlefshëm");
        return;
      }

      applyRows(result.rows);
      setLastSignature(signature);

      const outcome = describeGenerationOutcome(result, regenerate);
      toast.success(outcome.success);
      outcome.problems.forEach((problem) => toast.error(problem));
    } catch (error) {
      console.error("AI translate error:", error);
      toast.error("Ndodhi një gabim gjatë gjenerimit të përkthimeve");
    } finally {
      busy.current = false;
      setGenerating(false);
    }
  }, [applyRows, getValues, lastSignature, sourceLanguage]);

  // Forgets what was generated, so the stale hint clears and the next run starts fresh.
  const resetGeneration = useCallback(() => {
    setLastResult(null);
    setLastWarnings([]);
    setLastSignature(null);
  }, []);

  return { generating, lastResult, lastWarnings, lastSignature, generate, resetGeneration };
};

export default useBroadcastTranslate;
